import { FC, useState, useEffect } from 'react';
import { ExtendedGridColDef } from '@/types/ExtendedGridColDef';
import { Grid, Typography, Paper, Box, Chip, Divider } from '@mui/material';
import { SCUIButton, SCUINoRecordComponent, SCUILoadingLinear } from '../../../atomic/atoms';
import { useTranslation } from 'react-i18next';
import { SCUITextFieldWithSelectBox } from '../../../atomic/molecules';
import { SCUIBreadCrumb, SCUICustomDataGrid, SCUISelectFieldWithButton } from '../../../atomic/molecules';
import { CargoQuery, PostCheckoutQuery } from '../../../services/query';
import TimeHelper from '../../../utils/time';
import Helper from '../../../utils/helper';
import { downloadExcel } from '../../../utils/excelFileHelper';
import Swal from 'sweetalert2';
import { SCUIContent } from '../../../atomic/organisms';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SendIcon from '@mui/icons-material/Send';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ChecklistIcon from '@mui/icons-material/Checklist';
import {
    readBlobAsDataUrl,
    sendReturnRejectDeliveryList,
    updateReturnStatuses,
} from './ReturnRejectEntegration.helper';

export const ReturnRejectEntegration: FC<any> = () => {
    const [datas, setDatas] = useState<any>();
    const [datasFirst, setDatasFirst] = useState<any>();
    const [selectedRows, setSelectedRows] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedShippingNo, setSelectedShippingNo] = useState('');
    const { t } = useTranslation();
    const [cargoNameList, setCargoNameList] = useState<any>();
    const [documentType, setDocumentType] = useState(1);
    const [selectedCargo, setSelectedCargo] = useState(0);
    const [refresh, setRefresh] = useState(false);

    const GetCargoList = async () => {
        try {
            let cargoList: any = await CargoQuery.CompanyList();
            setCargoNameList(cargoList.data);
        } catch (error) {
            console.log('Hata :>> ', error);
        }
    };

    const SendDeliveryList = async () => {
        downloadExcel({
            excelDownloadData: selectedRows,
            excelDownloadFolderName: 'ReturnRejectList' + new Date().toJSON().slice(0, 10).replace(/-/g, '_'),
        });

        const ExcelJS = (await import('exceljs')).default;
        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet('ReturnRejectList');
        if (selectedRows.length > 0) {
            sheet.columns = Object.keys(selectedRows[0]).map((key) => ({ header: key, key }));
            selectedRows.forEach((row: any) => sheet.addRow(row));
        }
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/octet-stream' });
        const base64String = await readBlobAsDataUrl(blob);

        await updateReturnStatuses(selectedRows, () => {
            Swal.fire(t('general.statusUpdateFailed'), ' ', 'error');
        });
        await sendReturnRejectDeliveryList(
            base64String,
            selectedCargo,
            Helper.isProdEnv() ? t('return.returnRejectionList') : t('return.returnRejectionListTest')
        );

        Swal.fire(t('emailTemplateBuilder.mailSend'), ' ', 'success');
        setRefresh((prevLoading) => !prevLoading);
    };

    const GetRejectedReturnList = () => {
        setLoading(true);
        PostCheckoutQuery.GetRejectedReturnList({
            shippingcompanyid: selectedCargo,
        }).then((res: any) => {
            let new_array = res.data.map((item: any) => {
                return {
                    siparisNo: item.orderNumber,
                    sevkiyatNo: item.shippingListNo,
                    kargoSevkiyatKod: item.cargoShipmentCode,
                    siparisRedTarih: TimeHelper.formatDateHour(item.rejectedReturnDate),
                    musteriId: item.customerId,
                    kargoFirmasi: item.shippingCompanyName,
                    iadeId: item.returnId,
                };
            });
            setLoading(false);
            setDatas(new_array);
            setDatasFirst(new_array);
        });
    };

    useEffect(() => {
        GetCargoList();
    }, []);
    useEffect(() => {
        if (selectedCargo !== 0) {
            sorgula();
        }
    }, [refresh]);

    const columns: ExtendedGridColDef[] = [
        { field: 'sevkiyatNo', headerName: t('general.shippingNo'), flex: 1 },
        { field: 'siparisNo', headerName: t('general.orderNo'), flex: 1 },
        {
            field: 'musteriId',
            headerName: t('general.customerId'),
            flex: 1,
            renderCell: (params) => {
                const onclick = () => {
                    window.open(
                        import.meta.env.VITE_APPURL! +
                            '/customerManagement?ActiveTab=CustomerInformation&customerId=' +
                            params.row.musteriId,
                        '_blank'
                    );
                };
                return (
                    <div>
                        <p className="linktxt" onClick={onclick}>
                            <b>{params.row.musteriId}</b>{' '}
                        </p>
                    </div>
                );
            },
        },
        { field: 'kargoSevkiyatKod', headerName: t('general.cargoShipmentCode'), flex: 1 },
        { field: 'siparisRedTarih', headerName: t('general.orderRejectionDate'), flex: 1 },
        {
            field: 'iadeId',
            headerName: t('return.returnId'),
            flex: 1,
            hide: false,
            renderCell: (params) => {
                const onclick = () => {
                    window.open(
                        import.meta.env.VITE_APPURL! +
                            '/returnDetail?customerId=' +
                            params.row.musteriId +
                            '&ReturnId=' +
                            params.row.iadeId,
                        '_blank'
                    );
                };
                return (
                    <div>
                        <p className="linktxt" onClick={onclick}>
                            <b>{params.row.iadeId}</b>{' '}
                        </p>
                    </div>
                );
            },
        },
        { field: 'kargoFirmasi', headerName: t('general.cargoCompany'), flex: 1 },
    ];

    const sorgula = async () => {
        GetRejectedReturnList();
    };

    const barcodeReader = async () => {
        if (!Helper.isValueExistsInJSONArray(selectedRows, 'shippingListNo', selectedShippingNo)) {
            setSelectedRows(() => [
                ...datasFirst.filter(
                    (o: any) =>
                        (documentType === 1 ? o.siparisNo : o.sevkiyatNo).indexOf(selectedShippingNo.trim()) !== -1
                ),
            ]);
        }
    };

    const handleSelectionModelChange = (model: any[]) => {
        const selectedData = model.map((iadeId: any) => {
            return datasFirst.find((data: any) => data.kargoSevkiyatKod === iadeId);
        });
        setSelectedRows(selectedData);
    };

    const requestSearchOpr = (searchedVal: any) => {
        let filtered = datasFirst.filter((o: any) =>
            (documentType === 1 ? o.siparisNo : o.sevkiyatNo).includes(searchedVal)
        );
        setDatas(filtered);
    };

    return (
        <SCUIContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {/* BreadCrumb — untouched */}
                <Grid container direction="row">
                    <SCUIBreadCrumb title={t('return.returnRejectionList')} componentLink={'ReturnRejectEntegration'} />
                </Grid>

                <SCUILoadingLinear display={loading} />

                {/* Cargo company selector card */}
                <Paper
                    elevation={0}
                    sx={{
                        border: '1px solid',
                        borderColor: 'grey.200',
                        borderRadius: 3,
                        px: 2.5,
                        py: 2,
                        boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: '16px' }}>
                        <LocalShippingIcon sx={{ fontSize: 17, color: 'primary.main' }} />
                        <Typography fontWeight={700} fontSize={13} color="text.primary">
                            {t('cargoManagement.selectCargoCompany')}
                        </Typography>
                    </Box>
                    <SCUISelectFieldWithButton
                        selectPlaceHolder={t('cargoManagement.selectCargoCompany')}
                        selectValue={selectedCargo}
                        selectId={'id'}
                        selectText={'name'}
                        onChangeSelect={(event: any) => {
                            setSelectedCargo(event.target.value as number);
                        }}
                        selectList={cargoNameList}
                        onClickButton={() => sorgula()}
                        onKeyPressTextField={(e: any) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                sorgula();
                            }
                        }}
                        buttonText={t('general.list')}
                    />
                </Paper>

                {datas ? (
                    <>
                        {/* Search by order/shipping no */}
                        <Box sx={{ mb: 0.5 }}>
                            <SCUITextFieldWithSelectBox
                                placeholder={' '}
                                selectList={[
                                    { value: 1, text: t('general.orderNo') },
                                    { value: 2, text: t('general.shippingNo') },
                                ]}
                                onChangeSelect={(event: any) => {
                                    setDocumentType(event.target.value as number);
                                }}
                                selectValue={documentType}
                                onChangeTextField={(event: any) => {
                                    setSelectedShippingNo(event.target.value.trim());
                                    requestSearchOpr(event.target.value.trim());
                                }}
                                onClickButton={() => barcodeReader()}
                                onKeyPressTextField={(e: any) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        barcodeReader();
                                    }
                                }}
                            />
                        </Box>

                        {/* DataGrid — untouched */}
                        <SCUICustomDataGrid
                            excelData={(data: any) => console.info(data)}
                            selectionOnClick={true}
                            pageSize={50}
                            checkboxSelection
                            rows={datas}
                            getRowId={(row: any) => row.kargoSevkiyatKod}
                            columns={columns}
                            onSelectionModelChange={handleSelectionModelChange}
                            selectionModel={selectedRows.map((row) => row.kargoSevkiyatKod)}
                            excelDownloadButton={true}
                            excelDownloadButtonName={t('general.exportToExcel')}
                            excelDownloadData={selectedRows && selectedRows.length > 0 ? selectedRows : datas}
                            excelDownloadFolderName={
                                Helper.isProdEnv()
                                    ? t('return.returnRejectionList')
                                    : t('return.returnRejectionListTest')
                            }
                        />

                        {/* Warning notices */}
                        <Paper
                            elevation={0}
                            sx={{
                                border: '1px solid',
                                borderColor: 'warning.light',
                                borderLeft: '4px solid',
                                borderLeftColor: 'warning.main',
                                borderRadius: 2,
                                px: 2.5,
                                py: 1.5,
                                backgroundColor: 'rgba(255,167,38,0.05)',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1,
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <WarningAmberIcon sx={{ fontSize: 17, color: 'warning.main', flexShrink: 0 }} />
                                <Typography fontSize={13} fontWeight={600} color="warning.dark">
                                    {t('general.popupPermissionRequired')}
                                </Typography>
                            </Box>
                            <Divider sx={{ borderColor: 'warning.light' }} />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <WarningAmberIcon sx={{ fontSize: 17, color: 'warning.main', flexShrink: 0 }} />
                                <Typography fontSize={13} fontWeight={600} color="warning.dark">
                                    {t('general.doNotLeavePageBeforePrinting')}
                                </Typography>
                            </Box>
                        </Paper>

                        {/* Selected count + send button row */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                flexWrap: 'wrap',
                                gap: 2,
                                mb: 3,
                            }}
                        >
                            <Chip
                                icon={<ChecklistIcon />}
                                label={`${t('general.totalOrderCount') || 'Toplam Sipariş Sayısı'}: ${selectedRows.length}`}
                                size="medium"
                                sx={{
                                    fontSize: 13,
                                    fontWeight: 700,
                                    height: 34,
                                    backgroundColor: selectedRows.length > 0 ? 'rgba(25,118,210,0.08)' : 'grey.100',
                                    color: selectedRows.length > 0 ? 'primary.main' : 'text.secondary',
                                    border: '1px solid',
                                    borderColor: selectedRows.length > 0 ? 'rgba(25,118,210,0.25)' : 'grey.300',
                                    '& .MuiChip-icon': {
                                        fontSize: 18,
                                        color: selectedRows.length > 0 ? 'primary.main' : 'grey.400',
                                    },
                                }}
                            />
                            <SCUIButton
                                type="primary"
                                sizeType="fixed"
                                text={t('general.sendDeliveryListByEmail')}
                                size="s"
                                showStartIcon
                                startIcon={<SendIcon style={{ fontSize: '15px' }} />}
                                style={{
                                    height: '36px',
                                    padding: '0 20px',
                                    borderRadius: '8px',
                                    fontWeight: 600,
                                    fontSize: '13px',
                                }}
                                onClick={() => SendDeliveryList()}
                            />
                        </Box>
                    </>
                ) : (
                    <SCUINoRecordComponent />
                )}
            </Box>
        </SCUIContent>
    );
};
